import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Heading from 'components/atoms/Heading';
import Paragraph from 'components/atoms/Paragraph';
import Loading from 'components/atoms/Loading';
import Calendar from 'components/molecules/Calendar';
import { ReactComponent as Close } from 'assets/icons/close.svg';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { database } from 'helpers/firebase';
import Select from 'react-select';
import { customStyles, options } from 'helpers/constants';

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.08);
  z-index: 1;
`;

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-content: flex-start;
  align-items: flex-start;
  background-color: ${({ theme }) => theme.gray5};
  min-width: 560px;
  max-height: 80vh;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.16);
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 25px;
  width: 100%;
`;

const ButtonIcon = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  background-color: transparent;
  cursor: pointer;
  fill: ${({ theme }) => theme.textSecondary};
  height: 48px;
  width: 48px;
  border-radius: 50%;
  outline: none;

  :hover {
    background-color: ${({ theme }) => theme.gray4};
    fill: ${({ theme }) => theme.text};
  }

  svg {
    height: 32px;
    width: 32px;
  }
`;

const Label = styled.label`
  position: relative;
  margin: 10px 0;
`;

const Placeholder = styled.p`
  position: absolute;
  top: 15px;
  left: 10px;
  font-size: ${({ theme }) => theme.body};
  color: ${({ theme }) => theme.textSecondary};
  padding: 2px 4px;
  border-radius: 5px;
  pointer-events: none;
  transition: transform 0.15s ease-in-out, background-color 0.15s ease-in-out;

  ${({ top }) =>
    top &&
    css`
      transform: translate(-5px, -32px);
      background-color: ${({ theme }) => theme.gray4};
    `};
`;

const TextInput = styled.input`
  height: 48px;
  width: 300px;
  background-color: ${({ theme }) => theme.gray5};
  border: 2px solid ${({ theme }) => theme.gray4};
  border-radius: 5px;
  font-size: ${({ theme }) => theme.body};
  font-family: 'Poppins', sans-serif;
  padding: 0 10px;
  margin-top: 5px;
  color: ${({ theme }) => theme.text};
  outline: none;
  transition: border-color 0.15s ease-in-out;

  :focus {
    border-color: ${({ theme }) => theme.gray3};
  }
`;

const Textarea = styled.textarea`
  min-height: 180px;
  min-width: 500px;
  max-height: 380px;
  max-width: 500px;
  width: 100%;
  background-color: ${({ theme }) => theme.gray5};
  border: 2px solid ${({ theme }) => theme.gray4};
  border-radius: 5px;
  font-size: ${({ theme }) => theme.body};
  font-family: 'Poppins', sans-serif;
  padding: 10px;
  margin-top: 5px;
  color: ${({ theme }) => theme.text};
  outline: none;
  transition: border-color 0.15s ease-in-out;

  :focus {
    border-color: ${({ theme }) => theme.gray3};
  }
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.blue};
  border: none;
  border-radius: 10px;
  padding: 8px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.16);
  cursor: pointer;
  outline: none;
  transition: background-color 0.05s ease-in-out;
  margin-top: 20px;

  :hover:not(:disabled) {
    background-color: ${({ theme }) => theme.blueHover};
  }

  :disabled {
    opacity: 0.38;
    cursor: default;
  }
`;

function Modal({ hideModal, id }) {
  const modalRef = useRef(null);

  const [name, setName] = useState('');
  const [person, setPerson] = useState('');
  const [type, setType] = useState(options[0]);
  const [description, setDescription] = useState('');
  const [day, setDay] = useState(new Date().getDate());
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isNoDate, setIsNoDate] = useState(false);

  useOutsideClick(modalRef, () => hideModal());

  useEffect(() => {
    setIsDisabled(name.length === 0);
  }, [name]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      const ref = database.ref(`tasks/${id}`);

      ref.once('value').then((snapshot) => {
        const {
          name: n,
          description: desc,
          ts,
          isNoDate: isN,
          type: t,
          person: p,
        } = snapshot.val();
        const date = new Date(ts);

        const d = date.getDate();
        const m = date.getMonth();
        const y = date.getFullYear();

        setName(n);
        setDescription(desc);
        setDay(d);
        setMonth(m);
        setYear(y);
        setIsLoading(false);
        setType(options.find(({ value }) => value === t));
        setIsNoDate(isN);
        setPerson(p);
      });
    }
  }, [id]);

  const handleAddTask = () => {
    setIsLoading(true);

    const date = new Date(year, month, day);
    const ts = date.getTime();

    if (id) {
      const ref = database.ref(`/tasks/${id}`);

      ref.update(
        {
          name,
          description,
          ts,
          isNoDate,
          type: type.value,
          person,
        },
        () => {
          hideModal();
          setIsLoading(false);
        },
      );
    } else {
      const ref = database.ref('/tasks');

      ref.push(
        {
          name,
          description,
          ts,
          isNoDate,
          type: type.value,
          person,
        },
        () => {
          hideModal();
          setIsLoading(false);
        },
      );
    }
  };

  return (
    <Background>
      <Wrapper ref={modalRef}>
        <Header>
          <Heading>{id ? 'Edytuj' : 'Dodaj'} zadanie</Heading>
          <ButtonIcon type="button" onClick={() => hideModal()}>
            <Close />
          </ButtonIcon>
        </Header>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <Label>
              <Placeholder top={name.length}>Przedmiot</Placeholder>
              <TextInput type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </Label>
            <Label>
              <Placeholder top={person.length}>Nauczyciel</Placeholder>
              <TextInput type="text" value={person} onChange={(e) => setPerson(e.target.value)} />
            </Label>
            <Label />
            <Select
              isClearable
              isLoading={isLoading}
              isDisabled={isLoading}
              onChange={(newValue) => {
                setType(newValue);
              }}
              options={options}
              value={type}
              styles={customStyles}
              placeholder="Szukaj..."
            />
            <Label />
            <Label htmlFor="noDate">
              <input
                type="checkbox"
                id="noDate"
                checked={isNoDate}
                onChange={() => setIsNoDate((p) => !p)}
              />
              Data nieokreślona
            </Label>
            {!isNoDate && (
              <Calendar
                day={day}
                setDay={setDay}
                month={month}
                setMonth={setMonth}
                year={year}
                setYear={setYear}
              />
            )}
            <Label>
              <Placeholder top={description.length}>Opis zadania</Placeholder>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
            </Label>
            <Button type="button" onClick={handleAddTask} disabled={isDisabled}>
              <Paragraph>{id ? 'Edytuj' : 'Dodaj'} zadanie</Paragraph>
            </Button>
          </>
        )}
      </Wrapper>
    </Background>
  );
}

Modal.propTypes = {
  hideModal: PropTypes.func.isRequired,
  id: PropTypes.string,
};

Modal.defaultProps = {
  id: null,
};

export default Modal;
